import { Box, Grid, Typography, Paper, IconButton, useTheme, Switch, FormControlLabel } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Label, Legend, LabelList } from "recharts";
import { FiDownload, FiX } from "react-icons/fi";
import { fetchWithToken } from "../../authentication/AuthProvider";
import { MainContainer } from "../../components/container/MainContainer";
import { PageTitleContainer } from "../../components/container/PageTitleContainer";

import config from "../../config";
import { useProjectsAll } from "../../utilities/useProjectQueries";
import "../../style/pages-style/Comparison.css";
import MetricCard from "../../app_components/MetricCard";
import { LuPackage, LuWrench, LuActivity, LuTrash2, LuRepeat, LuFactory, LuFootprints } from "react-icons/lu";

import AgGrid from "../../components/grid/AgGrid";
import {
  centerAlignColDef,
  defaultColDef,
  floatFormater,
  leftAlignColDef,
  rightAlignColDef,
} from "../../components/grid/columnDefinitions/columnDefinitions";
import HorizontalStackedBarChart from "../../components/chart/HorizontalStackedBarChart";
import { GRAPH_PALETTE, getGraphColorByStage } from "../../theme/style/graphChartPalette";
import { getOrgTokens } from "../../theme/style/organizationPalette";

// const MetricCard = ({ label, value, average, unit = "kg CO₂e" }) => {
//   return (
//     <Paper
//       elevation={0}
//       sx={{
//         p: 2,
//         borderRadius: "12px",
//         border: "1px solid",
//         borderColor: "divider",
//         backgroundColor: "background.paper",
//         height: "100%",
//         display: "flex",
//         flexDirection: "column",
//         gap: 0.5,
//       }}
//     >
//       <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "none", fontSize: "0.8rem" }}>
//         {label}
//       </Typography>
//       <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
//         <Typography variant="h4" sx={{ color: "#659d33", fontWeight: 700, fontSize: "1.75rem" }}>
//           {value}
//         </Typography>
//         <Typography component="span" variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
//           {unit}
//         </Typography>
//       </Box>
//       <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem", mt: 0.5 }}>
//         Average: <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>{average} {unit}</Box>
//       </Typography>
//     </Paper>
//   );
// };

function ProjectComparison() {
  const comparisonType = "Utilizer";
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const [comparisonBasedOn, setComparisonBasedOn] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);
  const [comparisonBasedOnOptions, setComparisonBasedOnOptions] = useState([]);
  const [rawComparisonData, setRawComparisonData] = useState([]);
  const [showBenchmark, setShowBenchmark] = useState(true);

  const { data: cachedAllProjects = [] } = useProjectsAll();

  useEffect(() => {
    if (cachedAllProjects) {
      setComparisonBasedOnOptions(
        cachedAllProjects
          .map((i) => ({ value: i.id, label: i.name || i.id }))
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    }
  }, [cachedAllProjects]);

  const handleApply = () => {
    const allSelectedValues = Array.from(new Set([comparisonBasedOn, ...selectedValues])).filter(Boolean);

    if (allSelectedValues.length > 0) {
      const queryParams = new URLSearchParams({
        comparison_type: comparisonType,
        selected_values: allSelectedValues.join(","),
      });

      fetchWithToken(`${config.apiUrl}/get_gwp_per_lca_phase?${queryParams.toString()}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      })
        .then((response) => response.json())
        .then((data) => {
          // Flatten results and ensure we don't have duplicates if the backend returns any
          const formatted = Object.entries(data).map(([rowId, phases]) => {
            const productOption = comparisonBasedOnOptions.find((option) => String(option.value) === String(rowId));
            const productName = productOption ? productOption.label : rowId;
            return {
              id: rowId,
              name: productName,
              productPhase: parseFloat(phases["Product Phase"] || 0),
              constructionPhase: parseFloat(phases["Construction Phase"] || 0),
              usePhase: parseFloat(phases["Use Phase"] || 0),
              endOfLifePhase: parseFloat(phases["End-of-life Phase"] || 0),
              benefitsPhase: parseFloat(phases["Benefits Phase"] || 0),
              total: parseFloat(
                (phases["Product Phase"] || 0) +
                (phases["Construction Phase"] || 0) +
                (phases["Use Phase"] || 0) +
                (phases["End-of-life Phase"] || 0) +
                (phases["Benefits Phase"] || 0)
              ),
            };
          });

          // Ensure baseline is prioritized in the list if present
          setRawComparisonData(formatted);
        })
        .catch(console.error);
    } else {
      setRawComparisonData([]);
    }
  };

  useEffect(() => {
    if (comparisonBasedOn || selectedValues.length > 0) {
      handleApply();
    }
  }, [comparisonBasedOn, selectedValues]);

  const averages = useMemo(() => {
    if (rawComparisonData.length === 0) return { productPhase: 0, constructionPhase: 0, usePhase: 0, endOfLifePhase: 0, benefitsPhase: 0 };
    const count = rawComparisonData.length;
    return {
      productPhase: (rawComparisonData.reduce((acc, curr) => acc + curr.productPhase, 0) / count),
      constructionPhase: (rawComparisonData.reduce((acc, curr) => acc + curr.constructionPhase, 0) / count),
      usePhase: (rawComparisonData.reduce((acc, curr) => acc + curr.usePhase, 0) / count),
      endOfLifePhase: (rawComparisonData.reduce((acc, curr) => acc + curr.endOfLifePhase, 0) / count),
      benefitsPhase: (rawComparisonData.reduce((acc, curr) => acc + curr.benefitsPhase, 0) / count),
    };
  }, [rawComparisonData]);

  const selectedItemData = useMemo(() => {
    return rawComparisonData.find(d => String(d.id) === String(comparisonBasedOn)) || {
      productPhase: 0, constructionPhase: 0, usePhase: 0, endOfLifePhase: 0, benefitsPhase: 0, total: 0, name: t("Baseline")
    };
  }, [rawComparisonData, comparisonBasedOn, t]);

  const comparisonChartData = useMemo(() => {
    const avgTotal = rawComparisonData.length ? (rawComparisonData.reduce((acc, curr) => acc + curr.total, 0) / rawComparisonData.length) : 0;
    return [
      { label: t("Total GWP"), Baseline: selectedItemData.total, Average: avgTotal },
      { label: t("Product"), Baseline: selectedItemData.productPhase, Average: parseFloat(averages.productPhase || 0) },
      { label: t("Construction"), Baseline: selectedItemData.constructionPhase, Average: parseFloat(averages.constructionPhase || 0) },
      { label: t("Use"), Baseline: selectedItemData.usePhase, Average: parseFloat(averages.usePhase || 0) },
      { label: t("End-of-life"), Baseline: selectedItemData.endOfLifePhase, Average: parseFloat(averages.endOfLifePhase || 0) },
    ];
  }, [selectedItemData, averages, rawComparisonData, t]);

  const gridData = useMemo(() => {
    return rawComparisonData.map(d => ({
      id: d.id,
      name: d.name,
      product_phase: d.productPhase,
      construction_phase: d.constructionPhase,
      use_phase: d.usePhase,
      eol_phase: d.endOfLifePhase,
      benefits_phase: d.benefitsPhase,
      total: d.total
    }));
  }, [rawComparisonData]);

  const colDefs = useMemo(() => [
    { headerName: t("Project Name"), field: "name", ...leftAlignColDef, minWidth: 200, flex: 1, cellStyle: { color: "#659d33", fontWeight: 600 } },
    { headerName: t("Product Phase"), field: "product_phase", ...rightAlignColDef, ...floatFormater },
    { headerName: t("Construction Phase"), field: "construction_phase", ...rightAlignColDef, ...floatFormater },
    { headerName: t("Use Phase"), field: "use_phase", ...rightAlignColDef, ...floatFormater },
    { headerName: t("End-of-life Phase"), field: "eol_phase", ...rightAlignColDef, ...floatFormater },
    { headerName: t("Benefits Phase"), field: "benefits_phase", ...rightAlignColDef, ...floatFormater },
    { headerName: t("Total GWP kg CO₂e"), field: "total", ...defaultColDef, ...rightAlignColDef, ...floatFormater, cellStyle: { ...rightAlignColDef.cellStyle, fontWeight: 700 } },
  ], [t]);

  const chartStackData = useMemo(() => {
    return rawComparisonData.map(d => ({
      label: d.name,
      "Product Phase": d.productPhase,
      "Construction Phase": d.constructionPhase,
      "Use Phase": d.usePhase,
      "End-of-life Phase": d.endOfLifePhase,
      "Benefits Phase": d.benefitsPhase,
    }));
  }, [rawComparisonData]);
  return (
    <MainContainer>
      {/* METRIC CARDS SECTION */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(auto-fit, minmax(220px, 1fr))", lg: "repeat(5, 1fr)" }, gap: 2.5 }}>
          {[
            { title: "Product Phase", key: "productPhase", icon: LuPackage, accent: "#2563eb", bg: "rgba(37, 99, 235, 0.12)" },
            { title: "Construction Phase", key: "constructionPhase", icon: LuWrench, accent: "#ea580c", bg: "rgba(234, 88, 12, 0.12)" },
            { title: "Use Phase", key: "usePhase", icon: LuActivity, accent: "#16a34a", bg: "rgba(22, 163, 74, 0.12)" },
            { title: "End-of-life Phase", key: "endOfLifePhase", icon: LuTrash2, accent: "#dc2626", bg: "rgba(220, 38, 38, 0.12)" },
            { title: "Benefits Phase", key: "benefitsPhase", icon: LuRepeat, accent: "#0d9488", bg: "rgba(13, 148, 136, 0.12)" },
          ].map((phase, index) => (
            <Box key={phase.key}>
              <MetricCard
                title={t(phase.title)}
                value={selectedItemData[phase.key] || 0}
                unit="kg CO₂e"
                icon={phase.icon}
                accent={phase.accent}
                bg={phase.bg}
                delay={index * 0.08}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* SELECTION SECTION */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontFamily: "Poppins", fontSize: "20px", fontWeight: 500, color: isDark ? "#fff" : "#0f172a", mb: 2, textAlign: 'left' }}>
          {t("Comparison Project Selection")}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, p: 3, borderRadius: "12px", bgcolor: tok.bg.card, border: "1px solid", borderColor: tok.border.card, boxShadow: tok.shadow.card, position: 'relative', zIndex: 10 }}>
          <Box>
            <Typography variant="caption" sx={{ display: "block", mb: 1, color: isDark ? "#a1a1aa" : "text.secondary", ml: 0.5, letterSpacing: "0.025em", fontWeight: 600 }}>
              {t("Baseline Project")}
            </Typography>
            <Select
              options={comparisonBasedOnOptions}
              value={comparisonBasedOnOptions.find(o => o.value === comparisonBasedOn)}
              onChange={(opt) => setComparisonBasedOn(opt ? opt.value : "")}
              placeholder={t("Select baseline project...")}
              isClearable
              menuPortalTarget={document.body}
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderRadius: "8px",
                  border: state.isFocused ? `1px solid #659d33` : `1px solid ${tok.border.card}`,
                  minHeight: "44px",
                  backgroundColor: isDark ? "#18181b" : "white",
                  boxShadow: "none",
                  fontSize: "0.85rem",
                  "&:hover": { borderColor: "#659d33" }
                }),
                menu: (base) => ({ ...base, backgroundColor: isDark ? "#18181b" : "white", zIndex: 9999 }),
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected ? "#659d33" : state.isFocused ? (isDark ? "rgba(101, 157, 51, 0.15)" : "rgba(101, 157, 51, 0.08)") : "transparent",
                  color: state.isSelected ? "white" : (isDark ? "white" : "black"),
                  "&:active": { backgroundColor: "#659d33" }
                }),
                singleValue: (base) => ({ ...base, color: isDark ? "white" : "black" }),
                input: (base) => ({ ...base, color: isDark ? "white" : "black" }),
                placeholder: (base) => ({ ...base, color: isDark ? "#a1a1aa" : "#64748b" })
              }}
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ display: "block", mb: 1, color: isDark ? "#a1a1aa" : "text.secondary", ml: 0.5, letterSpacing: "0.025em", fontWeight: 600 }}>
              {t("Comparison Projects")}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", minHeight: "44px" }}>
              {selectedValues.map((val) => {
                const opt = comparisonBasedOnOptions.find(o => o.value === val);
                if (!opt) return null;
                return (
                  <Box
                    key={val}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      pl: 2,
                      pr: 1.5,
                      py: 0.8,
                      borderRadius: "8px",
                      border: "1px solid",
                      borderColor: tok.border.card,
                      backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "white",
                      fontSize: "0.85rem",
                      color: tok.text.primary,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "#659d33",
                        boxShadow: "0 2px 8px rgba(101,157,51,0.15)"
                      }
                    }}
                  >
                    {opt.label}
                    <Box
                      component="span"
                      sx={{ cursor: "pointer", display: "flex", color: "text.secondary", transition: "color 0.2s", "&:hover": { color: "#ef4444" } }}
                      onClick={() => setSelectedValues(prev => prev.filter(v => v !== val))}
                    >
                      <FiX size={14} />
                    </Box>
                  </Box>
                );
              })}
              <Box sx={{ width: selectedValues.length > 0 ? "180px" : "100%" }}>
                <Select
                  options={comparisonBasedOnOptions.filter(o => o.value !== comparisonBasedOn && !selectedValues.includes(o.value))}
                  onChange={(opt) => opt && setSelectedValues(prev => [...prev, opt.value])}
                  placeholder={selectedValues.length > 0 ? t("Add more...") : t("Search and add projects to compare...")}
                  value={null}
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      border: state.isFocused ? `1px solid #659d33` : `1px solid ${tok.border.card}`,
                      minHeight: "44px",
                      backgroundColor: selectedValues.length > 0 ? (isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)") : (isDark ? "#18181b" : "white"),
                      fontSize: "0.85rem",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#659d33" }
                    }),
                    menu: (base) => ({ ...base, backgroundColor: isDark ? "#18181b" : "white", zIndex: 9999 }),
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? "#659d33" : state.isFocused ? (isDark ? "rgba(101, 157, 51, 0.15)" : "rgba(101, 157, 51, 0.08)") : "transparent",
                      color: state.isSelected ? "white" : (isDark ? "white" : "black"),
                      "&:active": { backgroundColor: "#659d33" }
                    }),
                    input: (base) => ({ ...base, color: isDark ? "white" : "black" }),
                    placeholder: (base) => ({ ...base, color: isDark ? "#a1a1aa" : "#64748b" })
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: tok.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card, overflow: "hidden", mb: 4 }}>
        <Box sx={{ p: 2, px: 3, borderBottom: `1px solid ${tok.border.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: tok.text.chartTitle, fontFamily: GRAPH_PALETTE.font.family }}>{t("Footprint Summary")}</Typography>
            <Typography sx={{ color: "text.secondary", fontSize: "0.75rem", fontStyle: "italic", pt: 0.5 }}>
              ({t("All units are calculated in kg CO₂e")})
            </Typography>
          </Box>
          <IconButton size="small"><FiDownload size={18} /></IconButton>
        </Box>
        <Box sx={{ width: '100%' }}>
          <AgGrid
            rowData={gridData}
            columnDefs={colDefs}
            domLayout={gridData.length > 20 ? "normal" : "autoHeight"}
            pagination={gridData.length > 20}
          />
        </Box>
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: showBenchmark ? { xs: "1fr", lg: "1fr 1fr" } : "1fr", gap: 4, mb: 4 }}>
        {/* PHASE BREAKDOWN CHART */}
        <Paper elevation={0} sx={{ p: 1, borderRadius: tok.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <Box sx={{ p: 2, px: 3, borderBottom: `1px solid ${tok.border.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle, textAlign: "left", m: 0, fontFamily: GRAPH_PALETTE.font.family }}>
              {t("Impact Distribution by Project")}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showBenchmark}
                    onChange={(e) => setShowBenchmark(e.target.checked)}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#659d33' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#659d33' },
                    }}
                  />
                }
                label={t("Benchmark Comparison")}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    fontWeight: 500
                  }
                }}
              />
              <IconButton size="small"><FiDownload size={18} /></IconButton>
            </Box>
          </Box>
          <HorizontalStackedBarChart
            data={chartStackData}
            dataKeys={["Product Phase", "Construction Phase", "Use Phase", "End-of-life Phase", "Benefits Phase"]}
            coloredBars={true}
            showLegend={false}
            height={Math.max(450, chartStackData.length * 60 + 100)}
            xAxisLabel={t("GWP Impact (kg CO₂e)")}
            yAxisLabel=""
            fillHeight={showBenchmark}
          />
        </Paper>

        {/* COMPARATIVE METRIC ANALYSIS (IMPROVISED) */}
        {showBenchmark && (
          <Paper elevation={0} sx={{ p: 1, borderRadius: tok.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card, overflow: "hidden", animation: "org-fadeInUp 0.4s ease", display: "flex", flexDirection: "column" }}>
          <Box sx={{ p: 2, px: 3, borderBottom: `1px solid ${tok.border.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle, textAlign: "left", m: 0, fontFamily: GRAPH_PALETTE.font.family }}>
              {t("Metric Benchmark Comparison")}
            </Typography>
            <IconButton size="small"><FiDownload size={18} /></IconButton>
          </Box>
          <Box sx={{ flexGrow: 1, minHeight: 450, p: 3, "& .recharts-cartesian-grid-horizontal line": { strokeOpacity: isDark ? 0.1 : 0.05 }, animation: "org-fadeIn 0.3s ease" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={comparisonChartData} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="label" 
                  axisLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.line : GRAPH_PALETTE.axes.lightMode.line, strokeWidth: GRAPH_PALETTE.axes.strokeWidth }}
                  tickLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.tick : GRAPH_PALETTE.axes.lightMode.tick }}
                  tick={{ 
                    fill: tok.text.chartTitle, 
                    fontSize: GRAPH_PALETTE.font.sizes.standard, 
                    fontFamily: GRAPH_PALETTE.font.family,
                    fontWeight: GRAPH_PALETTE.font.weights.standard 
                  }} 
                  dy={10}
                />
                <YAxis 
                  axisLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.line : GRAPH_PALETTE.axes.lightMode.line, strokeWidth: GRAPH_PALETTE.axes.strokeWidth }}
                  tickLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.tick : GRAPH_PALETTE.axes.lightMode.tick }}
                  tick={{ 
                    fill: tok.text.chartTitle, 
                    fontSize: GRAPH_PALETTE.font.sizes.standard, 
                    fontFamily: GRAPH_PALETTE.font.family 
                  }}
                  label={{ 
                    value: t('kg CO₂e'), 
                    angle: -90, 
                    position: 'insideLeft', 
                    style: { 
                      textAnchor: 'middle', 
                      fill: tok.text.chartTitle, 
                      opacity: 0.8, 
                      fontSize: GRAPH_PALETTE.font.sizes.mainAxisTitle, 
                      fontFamily: GRAPH_PALETTE.font.family,
                      fontWeight: GRAPH_PALETTE.font.weights.bold 
                    } 
                  }}
                />
                <Tooltip 
                  cursor={{ fill: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}
                  contentStyle={{ 
                    backgroundColor: tok.bg.card, 
                    border: `1px solid ${tok.border.card}`, 
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    fontFamily: GRAPH_PALETTE.font.family,
                    fontSize: GRAPH_PALETTE.font.sizes.standard
                  }}
                  itemStyle={{ fontSize: GRAPH_PALETTE.font.sizes.standard }}
                  formatter={(value, name) => [Number(value).toFixed(1), name]}
                />
                <Legend 
                  verticalAlign="top" 
                  align="right" 
                  iconType="circle"
                  wrapperStyle={{ 
                    paddingBottom: 30, 
                    fontFamily: GRAPH_PALETTE.font.family, 
                    fontSize: GRAPH_PALETTE.font.sizes.standard,
                    fontWeight: GRAPH_PALETTE.font.weights.medium 
                  }}
                />
                <Bar 
                  dataKey="Baseline" 
                  name={t("Baseline Project")} 
                  fill={GRAPH_PALETTE.lcaStages.A1} 
                  radius={[4, 4, 0, 0]} 
                  barSize={32}
                />
                <Bar 
                  dataKey="Average" 
                  name={t("Selection Average")} 
                  fill={GRAPH_PALETTE.lcaStages.A3} 
                  radius={[4, 4, 0, 0]} 
                  barSize={32}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
        )}
      </Box>
    </MainContainer>
  );
}

export default ProjectComparison;

